"use client";

import { constructionStatus } from "@/info/unit/constructionStatus";
import * as Slider from "@radix-ui/react-slider";
import { useState } from "react";

interface MaxAgeSectionProps {
	value: string;
	onChange: (value: string) => void;
	showShortLabel?: boolean;
}

// Get the Ready To Move In options for max age
const maxAgeScale = constructionStatus.find(status => status.value === "readyToMoveIn")?.options || [];

export function MaxAgeSection({ value, onChange, showShortLabel }: MaxAgeSectionProps) {
	const [showTooltip, setShowTooltip] = useState(false);

	// Find the closest index for the current value
	const findClosestIndex = (val: string) => {
		if (!val) return maxAgeScale.length - 1; // Default to last option when empty
		const index = maxAgeScale.findIndex(item => item.value === val);
		return index >= 0 ? index : maxAgeScale.length - 1;
	};

	const currentSliderIndex = findClosestIndex(value || "");

	const handleSliderChange = (newIndices: [number]) => {
		const newIndex = newIndices[0];
		const newValue = maxAgeScale[newIndex]?.value;
		if (newValue) {
			onChange(newValue);
		}
	};

	// Format value for tooltip display
	const formatValueLabel = (index: number) => {
		const item = maxAgeScale[index];
		return item ? item.label : "";
	};

	return (
		<div className="space-y-6">
			{/* Slider with Tooltip and Tick Marks */}
			<div className="relative pt-8">
				<div className="relative h-5">
					{/* Tooltip */}
					{showTooltip && (
						<div
							className="absolute -top-12 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10 pointer-events-none"
							style={{
								left: `${(currentSliderIndex / (maxAgeScale.length - 1)) * 100}%`,
								transform: "translateX(-50%)",
							}}
						>
							{formatValueLabel(currentSliderIndex)}
						</div>
					)}

					<Slider.Root
						className="relative flex items-center select-none touch-none w-full h-5"
						value={[currentSliderIndex]}
						onValueChange={handleSliderChange}
						onValueCommit={() => setShowTooltip(false)}
						min={0}
						max={maxAgeScale.length - 1}
						step={1}
					>
						<Slider.Track className="bg-gray-300 relative grow rounded-full h-1">
							<Slider.Range className="absolute bg-gray-500 rounded-full h-full" />
						</Slider.Track>
						<Slider.Thumb
							aria-label="Maximum Age"
							className="block w-6 h-6 bg-white border-2 border-gray-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
							onMouseDown={() => setShowTooltip(true)}
							onTouchStart={() => setShowTooltip(true)}
						/>
					</Slider.Root>
				</div>

				{/* Tick marks and labels */}
				<div className="relative mt-4 pb-4">
					{maxAgeScale.map((option, index) => {
						const isSelected = index === currentSliderIndex;

						const isFirst = index === 0;
						const isLast = index === maxAgeScale.length - 1;
						const leftPosition = (index / (maxAgeScale.length - 1)) * 100;

						return (
							<div
								key={option.value}
								className="absolute flex flex-col items-center"
								style={{
									left: `${leftPosition}%`,
									transform: isFirst ? "translateX(0%)" : isLast ? "translateX(-100%)" : "translateX(-50%)",
								}}
							>
								{/* Tick mark */}
								<div className={`w-px h-2 ${isSelected ? "bg-primary-600" : "bg-gray-400"}`} />
								{/* Label */}
								<span
									className={`text-xs font-semibold ${isSelected ? "text-primary-600" : "text-gray-600"} whitespace-nowrap`}
								>
									{showShortLabel ? option.shortLabel : option.label}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
